/**
 * Auth helper for AgentCore WebSocket connections.
 * Uses the official AWS SDK SignatureV4 presigner for correct SigV4 signing.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';

import config from 'src/config';

export async function getIdToken (): Promise<string | undefined> {
  try {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString();
  } catch {
    return undefined;
  }
}

export async function getSignedWebSocketUrl (sessionId: string, userId: string, idToken?: string): Promise<string> {
  const { credentials } = await fetchAuthSession();
  if (!credentials) throw new Error('No credentials available');

  const url = new URL(config.agentCoreWsUrl as string);

  const signer = new SignatureV4({
    service: 'bedrock-agentcore',
    region: config.region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    sha256: Sha256,
  });

  const request = new HttpRequest({
    method: 'GET',
    protocol: 'wss:',
    hostname: url.hostname,
    path: url.pathname,
    query: {
      'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionId,
      'X-Amzn-Bedrock-AgentCore-Runtime-Custom-UserId': userId,
      ...(idToken ? { 'X-Amzn-Bedrock-AgentCore-Runtime-Custom-IdToken': idToken } : {}),
    },
    headers: {
      host: url.hostname,
    },
  });

  const presigned = await signer.presign(request, { expiresIn: 300 });

  const signedUrl = new URL(`wss://${presigned.hostname}${presigned.path}`);
  for (const [key, value] of Object.entries(presigned.query ?? {})) {
    signedUrl.searchParams.set(key, value as string);
  }

  return signedUrl.toString();
}
